import { Action as BaseReduxAction } from 'redux';
import { ReduxQueryAction } from 'redux-query';

import { PatientLoginInfo, PatientChartInfo } from './common';
import { BookingState, State, SessionState } from './state';
import { RQLoginResponse, RQResponse } from './responses';
import { RecursivePartial } from './typesUtils';

type ActionBase<T extends ActionType> = {
  type: T;
};

export enum ActionType {
  /**
   * We cannot just import types from redux-query package. Instead, we have to explicitly set a
   * particular string, to make TypeScript happy
   */
  RequestAsync = '@@query/REQUEST_ASYNC',
  MutateAsync = '@@query/MUTATE_ASYNC',

  LoginSuccess = 'LoginSuccess',
  Logout = 'Logout',
  UpdateSession = 'UpdateSession',
  SetPatientInfo = 'SetPatientInfo',
  UpdateBookingPatientInfo = 'UpdateBookingPatientInfo',
  UpdateBookingInfo = 'UpdateBookingInfo',
  ClearBookingInfo = 'ClearBookingInfo',
}

export type ReduxQueryOptions = {
  dontCamelizeKeys?: boolean;
  headers?: {};
};

export type ReduxAction =
  | ClearBookingInfo
  | LoginSuccess
  | LogoutSuccess
  | UpdateSession
  | SetPatientInfo
  | UpdateBookingInfo
  | UpdateBookingPatientInfo;

export type ThunkAction = (dispatch: Dispatch, getState: GetState) => any;

/**
 * An enhanced version of Dispatch that understands async actions like thunks
 * and redux-query queries.
 */
export type Dispatch = {
  <T extends ReduxQueryAction, K>(action: T): Promise<RQResponse<K>>;
  <T extends ThunkAction>(action: T): any;
  <T extends BaseReduxAction>(action: T): T;
};

export type GetState = () => State;

export type Action = ReduxAction | Logout | UpdateSession | MutateAsync | RequestAsync;

export type RequestAsync = {
  url: string;
  body?: {};
  transform?: (responseJson: {}, responseText: string) => {};
  options?: any;
} & ActionBase<ActionType.RequestAsync>;

export type MutateAsync = {
  url: string;
  body?: {};
  transform?: (responseJson: {}, responseText: string) => {};
  options?: any;
} & ActionBase<ActionType.MutateAsync>;

export type SetPatientInfo = {
  type: ActionType.SetPatientInfo;
  payload: {
    patient: PatientChartInfo;
  };
};

export type UpdateBookingPatientInfo = {
  type: ActionType.UpdateBookingPatientInfo;
  payload: {
    patient: Partial<PatientChartInfo>;
  };
};

export type UpdateBookingInfo = {
  type: ActionType.UpdateBookingInfo;
  payload: RecursivePartial<BookingState>;
};

export type ClearBookingInfo = {
  type: ActionType.ClearBookingInfo;
};

export type Login = (
  loginInfo: PatientLoginInfo,
  practiceId: number,
) => (dispatch: Dispatch) => Promise<RQLoginResponse>;

export type LoginSuccess = {
  type: ActionType.LoginSuccess;
  payload: {
    chartId: number;
    userId: number;
  };
};

export type UpdateSession = {
  type: ActionType.UpdateSession;
  payload: Partial<SessionState>;
};

export type Logout = () => (dispatch: Dispatch) => Promise<void>;

export type LogoutSuccess = {
  type: ActionType.Logout;
};

export type SelectAvailability = (
  date: string,
  time: string,
  appointmentTypeId: number,
  serviceLocationId: number,
  serviceLocationPhysicianId: number,
) => (dispatch: Dispatch, getState: GetState) => void;
